import { DeleteTimeLogConfirmationDialog } from "@jugl-web/domain-resources/task-time-tracking/components/DeleteTimeLogConfirmationDialog";
import { ManageTaskTimeLogDialog } from "@jugl-web/domain-resources/task-time-tracking/components/ManageTaskTimeLogDialog";
import { TaskTimeLogModel } from "@jugl-web/rest-api/tasks/models/TaskTimeLog/TaskTimeLogModel";
import { useConfirmationDialogState } from "@jugl-web/utils/hooks/useConfirmationDialogState";
import { useMe } from "@web-src/modules/auth/hooks/useMe";
import { useUserRole } from "@web-src/modules/common/hooks/useUserRole";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { forwardRef, useImperativeHandle, useMemo, useState } from "react";

export interface TimeTrackingDialogsFlowHandle {
  onAddTimeLog: () => void;
  onEditTimeLog: (timeLog: TaskTimeLogModel) => void;
  onDeleteTimeLog: (timeLog: TaskTimeLogModel) => void;
}

interface TimeTrackingDialogsFlowProps {
  taskId: string;
  taskName: string;
  boardId: string | null;
  onAfterCreate?: (timeLog: TaskTimeLogModel) => void;
  onAfterUpdate?: (timeLog: TaskTimeLogModel) => void;
  onAfterDelete?: (timeLogId: string) => void;
}

export const TimeTrackingDialogsFlow = forwardRef<
  TimeTrackingDialogsFlowHandle,
  TimeTrackingDialogsFlowProps
>(
  (
    { taskId, taskName, boardId, onAfterCreate, onAfterUpdate, onAfterDelete },
    ref
  ) => {
    const { entityId } = useEntitySelectedProvider();
    const { id: meId = "" } = useMe();
    const { role } = useUserRole();

    const [manageTimeLogDialogState, setManageTimeLogDialogState] = useState<{
      isOpen: boolean;
      editingTimeLog?: TaskTimeLogModel;
    }>({ isOpen: false });

    const deleteTimeLogConfirmationState = useConfirmationDialogState<{
      timeLog: TaskTimeLogModel;
    }>();

    useImperativeHandle(ref, () => ({
      onAddTimeLog: () => {
        setManageTimeLogDialogState({ isOpen: true });
      },
      onEditTimeLog: (timeLog: TaskTimeLogModel) => {
        setManageTimeLogDialogState({ isOpen: true, editingTimeLog: timeLog });
      },
      onDeleteTimeLog: (timeLog: TaskTimeLogModel) => {
        deleteTimeLogConfirmationState.open({ metadata: { timeLog } });
      },
    }));

    const initialFormValues = useMemo(
      () => ({ selectedTask: { id: taskId, name: taskName } }),
      [taskId, taskName]
    );

    return (
      <>
        <ManageTaskTimeLogDialog
          entityId={entityId}
          meId={meId}
          boardId={boardId}
          userRole={role}
          isOpen={manageTimeLogDialogState.isOpen}
          isTaskSelectable={false}
          editingTimeLog={manageTimeLogDialogState.editingTimeLog}
          initialFormValues={initialFormValues}
          onAfterSubmit={
            manageTimeLogDialogState.editingTimeLog
              ? onAfterUpdate
              : onAfterCreate
          }
          onClose={() => {
            setManageTimeLogDialogState((prevState) => ({
              ...prevState,
              isOpen: false,
            }));
          }}
        />
        <DeleteTimeLogConfirmationDialog
          isOpen={deleteTimeLogConfirmationState.isOpen}
          entityId={entityId}
          taskId={taskId}
          timeLog={deleteTimeLogConfirmationState.metadata?.timeLog || null}
          onClose={deleteTimeLogConfirmationState.close}
          onAfterDelete={onAfterDelete}
        />
      </>
    );
  }
);
